<template>
  <div class="user-info-container">
    <!-- 左侧蓝色标记和标题 -->
    <div class="page-header">
      <div class="blue-bar"></div>
      <h2 class="page-title">用户列表</h2>
    </div>
    
    <!-- 搜索区域 -->
    <div class="search-area">
      <div class="search-inputs">
        <div class="search-item">
          <el-input
            v-model="searchForm.username"
            placeholder="用户姓名"
            prefix-icon="el-icon-search"
            clearable
          ></el-input>
        </div>
        <div class="search-item">
          <el-input
            v-model="searchForm.phone"
            placeholder="手机号"
            clearable
          ></el-input>
        </div>
        <div class="search-item">
          <el-input
            v-model="searchForm.unit_name"
            placeholder="单位名称"
            clearable
            @keyup.enter.native="handleSearch"
          ></el-input>
        </div>
        <div class="search-item">
          <el-select
            v-model="searchForm.role_type"
            placeholder="角色类型"
            clearable
          >
            <el-option
              v-for="role in roleOptions"
              :key="role.id"
              :label="role.description || role.roleName"
              :value="role.roleName">
              <span style="float: left">{{ role.description || role.roleName }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">{{ role.roleName }}</span>
            </el-option>
          </el-select>
        </div>

        <div class="search-buttons">
          <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
          <el-button v-permission="'basic:user-info:create'" type="primary" @click="showAddDialog" icon="el-icon-plus">用户录入</el-button>
        </div>
      </div>
    </div>
    
    <!-- 表格区域 -->
    <div class="table-container">
      <el-table
        :data="tableData"
        border
        stripe
        style="width: 100%"
        v-loading="loading"
        :header-cell-style="{background:'#f5f7fa', color:'#606266'}"
      >
        <el-table-column prop="username" label="用户姓名" min-width="120"></el-table-column>
        <el-table-column prop="phone" label="手机号" width="150"></el-table-column>
        <el-table-column label="角色类型" width="150">
          <template slot-scope="scope">
            {{ getRoleDisplayName(scope.row.roleName) }}
          </template>
        </el-table-column>
        <el-table-column prop="unitName" label="单位名称" min-width="200"></el-table-column>
        <el-table-column label="操作" width="220" fixed="right">
          <template slot-scope="scope">
            <el-button
              v-permission="'basic:user-info:edit'"
              size="mini"
              type="text"
              class="edit-btn"
              @click="handleEdit(scope.row)"
            >编辑</el-button>
            <el-button
              size="mini"
              type="text"
              class="reset-password-btn"
              @click="handleResetPassword(scope.row)"
            >重置密码</el-button>
            <el-button
              v-permission="'basic:user-info:delete'"
              size="mini"
              type="text"
              class="delete-btn"
              @click="handleDelete(scope.row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 30, 50]"
          :page-size="pagination.pageSize"
          layout="prev, pager, next, jumper, sizes, total"
          :total="pagination.total"
        >
        </el-pagination>
      </div>
    </div>
    
    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogStatus === 'add' ? '用户录入' : '编辑用户'"
      :visible.sync="dialogVisible"
      width="700px"
      :close-on-click-modal="false"
      :show-close="true"
      center
      class="user-dialog"
    >
      <el-form :model="userForm" :rules="rules" ref="userForm" label-width="100px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="用户名称" prop="username">
              <el-input v-model="userForm.username" placeholder="请输入"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="角色类型" prop="role_type">
              <el-select v-model="userForm.role_type" placeholder="请选择" @change="handleRoleChange">
                <el-option
                  v-for="role in roleOptions"
                  :key="role.id"
                  :label="role.description || role.roleName"
                  :value="role.roleName">
                  <span style="float: left">{{ role.description || role.roleName }}</span>
                  <span style="float: right; color: #8492a6; font-size: 13px">{{ role.roleName }}</span>
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="电话" prop="phone">
              <el-input v-model="userForm.phone" placeholder="请输入"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="所属单位" prop="unit_id">
              <el-select v-model="userForm.unit_id" placeholder="请选择单位" filterable @change="handleUnitChange">
                <el-option
                  v-for="item in unitOptions"
                  :key="item.id"
                  :label="item.unitName"
                  :value="item.id">
                </el-option>
              </el-select>
              <div v-if="unitOptions.length === 0" style="color: #909399; font-size: 12px; margin-top: 5px;">
                {{userForm.role_type ? '暂无单位数据' : '请先选择角色类型'}}
              </div>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20" v-if="isProjectDepartmentRole">
          <el-col :span="24">
            <el-form-item label="关联项目" prop="projectIds">
              <el-select 
                v-model="userForm.projectIds" 
                placeholder="请先选择单位，然后选择项目（支持多选）" 
                multiple
                filterable
                style="width: 100%"
                :disabled="!userForm.unit_id"
              >
                <el-option
                  v-for="project in projectOptions"
                  :key="project.id"
                  :label="project.projectName"
                  :value="project.id">
                </el-option>
              </el-select>
              <div v-if="!userForm.unit_id" style="color: #909399; font-size: 12px; margin-top: 5px;">
                请先选择单位
              </div>
              <div v-else-if="projectOptions.length === 0" style="color: #909399; font-size: 12px; margin-top: 5px;">
                该单位暂无项目数据
              </div>
              <div v-else-if="userForm.projectIds && userForm.projectIds.length > 0" style="color: #67C23A; font-size: 12px; margin-top: 5px;">
                已选择 {{ userForm.projectIds.length }} 个项目
              </div>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="签名图片" prop="signatureImagePath">
              <el-upload
                ref="signatureUpload"
                class="signature-uploader"
                :http-request="handleSignatureUpload"
                :before-upload="beforeFileUpload"
                :show-file-list="false"
                accept=".jpg,.jpeg,.png"
              >
                <img v-if="signaturePreview" :src="signaturePreview" class="signature-image" />
                <div v-else class="upload-placeholder">
                  <i class="el-icon-plus signature-uploader-icon"></i>
                </div>
                <div slot="tip" class="el-upload__tip">支持jpg/png文件，且不超过10MB</div>
              </el-upload>
              <div v-if="userForm.signatureImagePath && !signaturePreview" class="file-path">
                <el-tag size="small" closable @close="clearSignature">{{ userForm.signatureImagePath }}</el-tag>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </div>
    </el-dialog>
    
    <!-- 删除确认对话框 -->
    <el-dialog
      title="提示"
      :visible.sync="deleteDialogVisible"
      width="400px"
      center
      :close-on-click-modal="false"
    >
      <div class="delete-dialog-content">
        <i class="el-icon-warning-outline warning-icon"></i>
        <span>确认删除该用户信息吗？</span>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="deleteDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmDelete">确定</el-button>
      </div>
    </el-dialog>
    
    <!-- 重置密码确认对话框 -->
    <el-dialog
      title="提示"
      :visible.sync="resetPasswordDialogVisible"
      width="400px"
      center
      :close-on-click-modal="false"
    >
      <div class="delete-dialog-content">
        <i class="el-icon-warning-outline warning-icon"></i>
        <span>确认重置用户 <strong>{{ resetPasswordUsername }}</strong> 的密码为默认密码"000000"吗？</span>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="resetPasswordDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmResetPassword">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { query_users_pagination, save_user, del_user, reset_user_password } from '@/api/modules/user.js'
import { getAllUnits } from '@/api/modules/unit.js'
import { getAllRoles } from '@/api/modules/role.js'
import { upload_attachment } from '@/api/modules/attachment.js'
import { getProjectsByUnit } from '@/api/modules/projects.js'
import { getApiUrl } from '@/utils/env'

export default {
  name: 'UserInfo',
  data() {
    return {
      // 搜索表单
      searchForm: {
        username: '',
        phone: '',
        role_type: ''
      },
      // 加载状态
      loading: false,
      // 表格数据
      tableData: [],
      // 分页配置
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      // 对话框
      dialogVisible: false,
      dialogStatus: 'add', // 'add' 或 'edit'
      deleteDialogVisible: false,
      deleteId: null,
      resetPasswordDialogVisible: false,
      resetPasswordId: null,
      resetPasswordUsername: '',
      // 表单数据
      userForm: {
        id: null,
        username: '',
        phone: '',
        role: '',
        role_type: '',
        role_name: '',
        unit_id: '',
        unit_name: '',
        password: '',
        signatureImagePath: '',
        projectIds: []
      },
      // 单位选择选项
      unitOptions: [],
      // 角色选择选项
      roleOptions: [],
      // 项目选择选项
      projectOptions: [],
      // 角色类型映射
      roleTypeMap: {},
      // 角色名称到ID的映射
      roleNameToIdMap: {},
      // 角色ID到名称的映射
      roleIdToNameMap: {},
      // 签名图片预览
      signaturePreview: '',
      // 表单验证规则
      rules: {
        username: [
          { required: true, message: '请输入用户名称', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: '请输入联系电话', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ],
        role_type: [
          { required: true, message: '请选择角色类型', trigger: 'change' }
        ],
        unit_id: [
          { required: true, message: '请选择所属单位', trigger: 'change' }
        ],
        password: [
          { required: false, message: '请输入密码', trigger: 'blur' }
        ],
        projectIds: [
          // 取消项目部角色必须选择关联项目的限制
          // 项目关联将在创建项目时自动建立
        ]
      }
    }
  },
  computed: {
    // 判断当前选择的角色是否是项目部
    isProjectDepartmentRole() {
      if (!this.userForm.role_type) {
        return false
      }
      // 通过角色名称或描述判断是否是项目部
      const selectedRole = this.roleOptions.find(role => role.roleName === this.userForm.role_type)
      if (!selectedRole) {
        return false
      }
      // 判断角色名称或描述是否包含"项目"相关关键词
      const roleName = selectedRole.roleName || ''
      const description = selectedRole.description || ''
      return roleName.includes('PROJECT') || 
             roleName.includes('项目') || 
             description.includes('项目')
    }
  },
  created() {
    this.fetchUserList();
    this.fetchRoleList();
  },
  methods: {


    // 获取角色列表
    fetchRoleList() {
      getAllRoles()
        .then(response => {
if (response.data && response.data.success) {
            this.roleOptions = response.data.data || [];
            
            // 构建角色映射
            this.roleOptions.forEach(role => {
              this.roleTypeMap[role.roleName] = role.roleName;
              this.roleNameToIdMap[role.roleName] = role.id;
              this.roleIdToNameMap[role.id] = role.roleName;
            });
            
} else {
            console.error('获取角色列表失败:', response.data.message);
            this.$message.error(response.data.message || '获取角色列表失败');
          }
        })
        .catch(error => {
          console.error('获取角色列表失败:', error);
          this.$message.error('获取角色列表失败，请稍后重试');
        });
    },
    
    // 获取用户列表
    fetchUserList() {
      this.loading = true;
      
      // 构建查询参数 - 根据新接口调整参数名称
      const params = {
        page: this.pagination.currentPage - 1, // 后端从0开始，前端从1开始
        size: this.pagination.pageSize,
        sortBy: 'id',
        sortDir: 'desc'
      };
      
      // 添加搜索条件
      if (this.searchForm.username) {
        params.username = this.searchForm.username;
      }
      if (this.searchForm.phone) {
        params.phone = this.searchForm.phone;
      }
      if (this.searchForm.unit_name && this.searchForm.unit_name.trim()) {
        params.unitName = this.searchForm.unit_name.trim();
      }
      if (this.searchForm.role_type) {
        params.roleId = this.roleNameToIdMap[this.searchForm.role_type];
      }
      // 注意：unit_name搜索需要转换为unitId，这里暂时移除unit_name搜索
      // 如果需要按单位名称搜索，需要后端支持或者先获取单位ID
      
// 调用API获取用户列表
      query_users_pagination(params)
        .then(response => {
if (response.data && response.data.success) {
            // 数据直接在 data.content 中
            const responseData = response.data.data;
            this.tableData = responseData.content || [];
            
            // 更新分页信息 - 使用Spring Data分页对象的字段
            this.pagination = {
              currentPage: responseData.number + 1, // 后端从0开始，前端从1开始
              pageSize: responseData.size,
              total: responseData.totalElements
            };   
          } else {
            this.$message.error(response.data.message || '查询失败');
          }
        })
        .catch(error => {
          console.error('查询用户列表失败: ', error);
          this.$message.error('查询用户列表失败，请稍后重试');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    // 搜索方法
    handleSearch() {
      this.pagination.currentPage = 1; // 重置为第一页
      this.fetchUserList();
    },
    
    // 显示新增对话框
    showAddDialog() {
      this.dialogStatus = 'add';
      this.resetForm();
      this.dialogVisible = true;
      // 加载所有单位选项
      this.fetchUnitList();
    },
    
    // 编辑操作
    handleEdit(row) {
      this.dialogStatus = 'edit';
      
      // 映射新字段名到表单字段
      this.userForm = {
        id: row.id,
        username: row.username,
        phone: row.phone,
        unit_id: row.unitId,
        unit_name: row.unitName,
        role_type: row.roleName,
        role: row.roleId,
        password: '', // 编辑时不修改密码
        signatureImagePath: row.signatureImagePath || '',
        projectIds: row.projectIds || []
      };
      
      // 如果有签名图片路径，加载预览
      if (row.signatureImagePath) {
        this.loadSignaturePreview(row.signatureImagePath)
      } else {
        this.signaturePreview = ''
      }
      
      this.dialogVisible = true;
      
      // 加载所有单位列表
      this.fetchUnitList();
      
      // 如果是编辑模式且是项目部角色且已有单位ID，加载项目列表
      // 注意：需要在项目列表加载完成后再设置projectIds，确保选项存在
      if (this.userForm.unit_id && this.isProjectDepartmentRole) {
        this.fetchProjectList().then(() => {
          // 确保编辑时的项目ID能正确匹配到选项
          if (this.userForm.projectIds && this.userForm.projectIds.length > 0) {
            // 过滤掉不在当前单位项目列表中的项目ID
            const validProjectIds = this.userForm.projectIds.filter(id => 
              this.projectOptions.some(project => project.id === id)
            )
            if (validProjectIds.length !== this.userForm.projectIds.length) {
              console.warn('部分项目不在当前单位的项目列表中，已自动过滤')
              this.userForm.projectIds = validProjectIds
            }
          }
        })
      }
    },
    
    // 删除操作
    handleDelete(row) {
      this.deleteId = row.id;
      this.deleteDialogVisible = true;
    },
    
    // 确认删除
    confirmDelete() {
      this.loading = true;
      
      // 调用删除API
      del_user(this.deleteId)
        .then(response => {
if (response.data && response.data.success) {
            this.$message.success(response.data.message || '删除成功');
            // 删除成功后更新列表
            this.tableData = this.tableData.filter(item => item.id !== this.deleteId);
            this.pagination.total -= 1;
            this.deleteDialogVisible = false;
          } else {
            this.$message.error(response.data.message || '删除失败');
          }
        })
        .catch(error => {
          console.error('删除用户失败: ', error);
          this.$message.error('删除用户失败，请稍后重试');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    // 重置密码操作
    handleResetPassword(row) {
      this.resetPasswordId = row.id;
      this.resetPasswordUsername = row.username;
      this.resetPasswordDialogVisible = true;
    },
    
    // 确认重置密码
    confirmResetPassword() {
      this.loading = true;
      
      // 调用重置密码API
      reset_user_password(this.resetPasswordId)
        .then(response => {
if (response.data && response.data.success) {
            this.$message.success(response.data.message || '密码重置成功，新密码为"000000"');
            this.resetPasswordDialogVisible = false;
          } else {
            this.$message.error(response.data.message || '重置密码失败');
          }
        })
        .catch(error => {
          console.error('重置密码失败: ', error);
          this.$message.error('重置密码失败：' + (error.response?.data?.message || error.message || '未知错误'));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    // 重置表单
    resetForm() {
      this.userForm = {
        id: null,
        username: '',
        phone: '',
        role: '',
        role_type: '',
        role_name: '',
        unit_id: '',
        unit_name: '',
        password: '', // 密码可选，后端默认为"000000"
        signatureImagePath: '',
        projectIds: []
      };
      this.signaturePreview = ''
      if (this.$refs.userForm) {
        this.$refs.userForm.resetFields();
      }
    },
    
    // 提交表单
    submitForm() {
      this.$refs.userForm.validate(valid => {
        if (valid) {
          this.loading = true;
          
          // 准备提交的数据 - 按照新接口要求构建
          const submitData = {
            username: this.userForm.username,
            phone: this.userForm.phone,
            unitId: this.userForm.unit_id,
            roleId: this.roleNameToIdMap[this.userForm.role_type]
          };
          
          // 如果是编辑模式，添加用户ID
          if (this.dialogStatus === 'edit' && this.userForm.id) {
            submitData.id = this.userForm.id;
          }
          
          // 如果是新增模式且设置了密码，则添加密码字段
          if (this.dialogStatus === 'add' && this.userForm.password) {
            submitData.password = this.userForm.password;
          }
          
          // 如果有签名图片路径，添加签名图片字段
          if (this.userForm.signatureImagePath) {
            submitData.signatureImagePath = this.userForm.signatureImagePath;
          }
          
          // 如果是项目部角色，添加项目ID列表
          if (this.isProjectDepartmentRole && this.userForm.projectIds && this.userForm.projectIds.length > 0) {
            submitData.projectIds = this.userForm.projectIds;
          }
          
// 使用save_user保存用户信息
          save_user(submitData)
            .then(response => {
if (response.data && response.data.success) {
                this.$message.success(response.data.message || '保存成功');
                this.dialogVisible = false;
                // 重新加载列表
                this.fetchUserList();
              } else {
                this.$message.error(response.data.message || '保存失败');
              }
            })
            .catch(error => {
              console.error('保存用户失败: ', error);
              this.$message.error('保存用户失败，请稍后重试');
            })
            .finally(() => {
              this.loading = false;
            });
        } else {
          return false;
        }
      });
    },
    
    // 分页大小变化
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.fetchUserList();
    },
    
    // 页码变化
    handleCurrentChange(val) {
      this.pagination.currentPage = val;
      this.fetchUserList();
    },
    
    // 角色类型变化处理
    handleRoleChange(value) {
      // 设置role字段为对应的ID
      this.userForm.role = this.roleNameToIdMap[value];
      
      // 如果不是项目部角色，清空项目选择
      if (!this.isProjectDepartmentRole) {
        this.userForm.projectIds = [];
      } else if (this.userForm.unit_id) {
        // 如果是项目部角色且已选择单位，加载项目列表
        this.fetchProjectList();
      }
    },
    
    // 单位变化处理
    handleUnitChange(unitId) {
      // 如果是项目部角色，加载该单位的项目列表
      if (this.isProjectDepartmentRole && unitId) {
        this.userForm.projectIds = []; // 清空之前选择的项目
        this.fetchProjectList();
      } else {
        this.projectOptions = [];
        this.userForm.projectIds = [];
      }
    },
    
    // 获取角色显示名称（中文）
    getRoleDisplayName(roleName) {
      if (!roleName) return '-'
      const role = this.roleOptions.find(r => r.roleName === roleName)
      return role ? (role.description || roleName) : roleName
    },
    
    // 文件上传前验证
    beforeFileUpload(file) {
      const maxSize = 10 * 1024 * 1024 // 10MB
      const isImage = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg'
      
      if (!isImage) {
        this.$message.error('只能上传jpg/png格式的图片')
        return false
      }
      
      if (file.size > maxSize) {
        this.$message.error('文件大小不能超过10MB')
        return false
      }
      
      // 检查图片尺寸（Canvas处理大图片可能会导致性能问题）
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const img = new Image()
          img.onload = () => {
            const maxDimension = 4000 // 最大尺寸4000像素
            const maxPixels = 16000000 // 最大总像素数（4000*4000）
            
            if (img.width > maxDimension || img.height > maxDimension) {
              this.$message.error(`图片尺寸过大，宽度和高度不能超过${maxDimension}像素`)
              resolve(false)
            } else if (img.width * img.height > maxPixels) {
              this.$message.error(`图片像素数过大，总像素数不能超过${maxPixels.toLocaleString()}像素`)
              resolve(false)
            } else {
              resolve(true)
            }
          }
          img.onerror = () => {
            this.$message.error('图片格式错误，无法读取')
            resolve(false)
          }
          img.src = e.target.result
        }
        reader.onerror = () => {
          this.$message.error('文件读取失败')
          resolve(false)
        }
        reader.readAsDataURL(file)
      })
    },
    
    // 将图片背景处理为透明
    async processImageToTransparent(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const img = new Image()
          img.onload = () => {
            try {
              // 创建Canvas
              const canvas = document.createElement('canvas')
              canvas.width = img.width
              canvas.height = img.height
              const ctx = canvas.getContext('2d')
              
              // 绘制图片
              ctx.drawImage(img, 0, 0)
              
              // 获取图像数据
              const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
              const data = imageData.data
              
              // 采样边缘区域来确定背景色（不只是四个角，而是整个边缘）
              const edgeSamples = []
              const edgeWidth = Math.max(5, Math.floor(canvas.width * 0.05)) // 边缘采样宽度，至少5像素
              const edgeHeight = Math.max(5, Math.floor(canvas.height * 0.05))
              
              // 采样上边缘
              for (let x = 0; x < canvas.width; x++) {
                for (let y = 0; y < edgeHeight; y++) {
                  const index = (y * canvas.width + x) * 4
                  edgeSamples.push({
                    r: data[index],
                    g: data[index + 1],
                    b: data[index + 2]
                  })
                }
              }
              
              // 采样下边缘
              for (let x = 0; x < canvas.width; x++) {
                for (let y = canvas.height - edgeHeight; y < canvas.height; y++) {
                  const index = (y * canvas.width + x) * 4
                  edgeSamples.push({
                    r: data[index],
                    g: data[index + 1],
                    b: data[index + 2]
                  })
                }
              }
              
              // 采样左边缘
              for (let y = 0; y < canvas.height; y++) {
                for (let x = 0; x < edgeWidth; x++) {
                  const index = (y * canvas.width + x) * 4
                  edgeSamples.push({
                    r: data[index],
                    g: data[index + 1],
                    b: data[index + 2]
                  })
                }
              }
              
              // 采样右边缘
              for (let y = 0; y < canvas.height; y++) {
                for (let x = canvas.width - edgeWidth; x < canvas.width; x++) {
                  const index = (y * canvas.width + x) * 4
                  edgeSamples.push({
                    r: data[index],
                    g: data[index + 1],
                    b: data[index + 2]
                  })
                }
              }
              
              // 计算边缘颜色的平均值（背景色）
              const avgR = Math.round(edgeSamples.reduce((sum, c) => sum + c.r, 0) / edgeSamples.length)
              const avgG = Math.round(edgeSamples.reduce((sum, c) => sum + c.g, 0) / edgeSamples.length)
              const avgB = Math.round(edgeSamples.reduce((sum, c) => sum + c.b, 0) / edgeSamples.length)
              
// 计算颜色差异阈值
              // 对于接近白色的背景，使用较大的容差
              let colorTolerance = 90 // 增加初始容差值
              
              // 如果背景色接近白色（RGB值都大于200），使用更大的容差
              if (avgR > 200 && avgG > 200 && avgB > 200) {
                colorTolerance = 110 // 白色背景使用更大的容差
              }
              
              // 处理每个像素，将接近背景色的像素转换为透明
              for (let i = 0; i < data.length; i += 4) {
                const r = data[i]
                const g = data[i + 1]
                const b = data[i + 2]
                
                // 计算当前像素与背景色的欧氏距离
                const rDiff = Math.abs(r - avgR)
                const gDiff = Math.abs(g - avgG)
                const bDiff = Math.abs(b - avgB)
                
                // 计算颜色距离（欧氏距离）
                const colorDistance = Math.sqrt(rDiff * rDiff + gDiff * gDiff + bDiff * bDiff)
                
                // 如果颜色距离在容差范围内，认为是背景，设置为透明
                if (colorDistance <= colorTolerance) {
                  data[i + 3] = 0 // 完全透明
                } else {
                  // 对于接近容差边界的像素，使用渐变透明，使边缘更自然
                  const fadeStart = colorTolerance * 0.8 // 从80%容差开始渐变
                  if (colorDistance <= colorTolerance * 1.3 && colorDistance > fadeStart) {
                    const fadeRatio = (colorDistance - fadeStart) / (colorTolerance * 1.3 - fadeStart)
                    data[i + 3] = Math.round(255 * fadeRatio) // 渐变透明度
                  }
                }
              }
              
              // 二次处理：对于边缘区域，如果周围大部分像素都是透明的，也将其设为透明
              // 这样可以去除一些孤立的背景像素
              const edgeCleanupPasses = 3 // 增加边缘清理的迭代次数
              for (let pass = 0; pass < edgeCleanupPasses; pass++) {
                for (let y = 1; y < canvas.height - 1; y++) {
                  for (let x = 1; x < canvas.width - 1; x++) {
                    const index = (y * canvas.width + x) * 4
                    const currentAlpha = data[index + 3]
                    
                    // 如果当前像素不是完全透明的，检查周围像素
                    if (currentAlpha > 0) {
                      let transparentNeighbors = 0
                      let totalNeighbors = 0
                      
                      // 检查8个邻居像素
                      for (let dy = -1; dy <= 1; dy++) {
                        for (let dx = -1; dx <= 1; dx++) {
                          if (dx === 0 && dy === 0) continue
                          const neighborIndex = ((y + dy) * canvas.width + (x + dx)) * 4
                          totalNeighbors++
                          if (data[neighborIndex + 3] === 0) {
                            transparentNeighbors++
                          }
                        }
                      }
                      
                      // 如果周围超过50%的像素是透明的，且当前像素接近背景色，也设为透明
                      // 降低阈值，更激进地去除背景
                      if (transparentNeighbors / totalNeighbors > 0.5) {
                        const r = data[index]
                        const g = data[index + 1]
                        const b = data[index + 2]
                        const rDiff = Math.abs(r - avgR)
                        const gDiff = Math.abs(g - avgG)
                        const bDiff = Math.abs(b - avgB)
                        const colorDistance = Math.sqrt(rDiff * rDiff + gDiff * gDiff + bDiff * bDiff)
                        
                        // 增加容差范围
                        if (colorDistance <= colorTolerance * 1.5) {
                          data[index + 3] = 0
                        }
                      }
                    }
                  }
                }
              }
              
              // 第三次处理：专门处理角落区域，更激进地去除背景
              // 增加角落处理区域的大小（从10%增加到15%）
              const cornerSize = Math.max(30, Math.floor(Math.min(canvas.width, canvas.height) * 0.15))
              const corners = [
                { x: 0, y: 0, width: cornerSize, height: cornerSize }, // 左上
                { x: canvas.width - cornerSize, y: 0, width: cornerSize, height: cornerSize }, // 右上
                { x: 0, y: canvas.height - cornerSize, width: cornerSize, height: cornerSize }, // 左下
                { x: canvas.width - cornerSize, y: canvas.height - cornerSize, width: cornerSize, height: cornerSize } // 右下
              ]
              
              corners.forEach(corner => {
                for (let y = corner.y; y < corner.y + corner.height; y++) {
                  for (let x = corner.x; x < corner.x + corner.width; x++) {
                    const index = (y * canvas.width + x) * 4
                    const r = data[index]
                    const g = data[index + 1]
                    const b = data[index + 2]
                    const rDiff = Math.abs(r - avgR)
                    const gDiff = Math.abs(g - avgG)
                    const bDiff = Math.abs(b - avgB)
                    const colorDistance = Math.sqrt(rDiff * rDiff + gDiff * gDiff + bDiff * bDiff)
                    
                    // 在角落区域使用更大的容差（增加到2.0倍）
                    if (colorDistance <= colorTolerance * 2.0) {
                      data[index + 3] = 0
                    }
                  }
                }
              })
              
              // 第四次处理：专门处理底部边缘区域（可能的小三角残留）
              const bottomEdgeHeight = Math.max(40, Math.floor(canvas.height * 0.12))
              for (let y = canvas.height - bottomEdgeHeight; y < canvas.height; y++) {
                for (let x = 0; x < canvas.width; x++) {
                  const index = (y * canvas.width + x) * 4
                  const currentAlpha = data[index + 3]
                  
                  // 如果当前像素不是完全透明的，检查是否接近背景色
                  if (currentAlpha > 0) {
                    const r = data[index]
                    const g = data[index + 1]
                    const b = data[index + 2]
                    const rDiff = Math.abs(r - avgR)
                    const gDiff = Math.abs(g - avgG)
                    const bDiff = Math.abs(b - avgB)
                    const colorDistance = Math.sqrt(rDiff * rDiff + gDiff * gDiff + bDiff * bDiff)
                    
                    // 在底部边缘使用更大的容差
                    if (colorDistance <= colorTolerance * 2.2) {
                      data[index + 3] = 0
                    }
                  }
                }
              }
              
// 将处理后的数据写回Canvas
              ctx.putImageData(imageData, 0, 0)
              
              // 将Canvas转换为Blob
              canvas.toBlob((blob) => {
                if (blob) {
                  resolve(blob)
                } else {
                  reject(new Error('图片处理失败'))
                }
              }, 'image/png') // PNG格式支持透明
            } catch (error) {
              reject(error)
            }
          }
          img.onerror = () => reject(new Error('图片加载失败'))
          img.src = e.target.result
        }
        reader.onerror = () => reject(new Error('文件读取失败'))
        reader.readAsDataURL(file)
      })
    },

    // 上传签名图片
    async handleSignatureUpload(options) {
      const file = options.file
      
      try {
        // 确定relatedId：编辑模式使用用户ID，新增模式使用0（临时上传）
        let relatedId = null
        if (this.userForm.id) {
          // 编辑模式：使用用户ID
          relatedId = this.userForm.id
        } else {
          // 新增模式：使用0（临时上传）
          relatedId = 0
        }
        
        // 处理图片：将背景转换为透明
        let processedFile = file
        try {
          this.$message.info('正在处理图片...')
          processedFile = await this.processImageToTransparent(file)
          
          // 如果是图片，生成预览（使用处理后的图片）
          if (processedFile.type.startsWith('image/')) {
            const reader = new FileReader()
            reader.onload = (e) => {
              this.signaturePreview = e.target.result
            }
            reader.readAsDataURL(processedFile)
          }
        } catch (error) {
          console.warn('图片处理失败，使用原图:', error)
          // 如果处理失败，使用原图并生成预览
          if (file.type.startsWith('image/')) {
            const reader = new FileReader()
            reader.onload = (e) => {
              this.signaturePreview = e.target.result
            }
            reader.readAsDataURL(file)
          }
        }
        
        // 使用通用附件上传API
        const formData = new FormData()
        formData.append('file', processedFile, file.name || 'signature.png')
        formData.append('category', 'user-signature')
        formData.append('relatedId', relatedId)
        
        const response = await upload_attachment(formData)
        
        if (response.data && response.data.success) {
          this.userForm.signatureImagePath = response.data.data?.filePath || ''
          this.$message.success('签名图片上传成功')
          options.onSuccess(response.data)
        } else {
          this.$message.error(response.data?.message || '上传失败')
          options.onError(new Error(response.data?.message || '上传失败'))
        }
      } catch (error) {
        console.error('上传签名图片失败:', error)
        this.$message.error('上传失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        options.onError(error)
      }
    },
    
    // 加载签名图片预览
    loadSignaturePreview(filePath) {
      if (!filePath) {
        this.signaturePreview = ''
        return
      }
      
      // 如果是完整的URL（http/https开头），直接使用
      if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
        this.signaturePreview = filePath
        return
      }
      
      // 如果是data URL（base64），直接使用
      if (filePath.startsWith('data:')) {
        this.signaturePreview = filePath
        return
      }
      
      // 否则，使用附件下载接口
      const apiUrl = getApiUrl('/attachments/download')
      this.signaturePreview = `${apiUrl}?filePath=${encodeURIComponent(filePath)}`
    },
    
    // 清除签名图片
    clearSignature() {
      this.userForm.signatureImagePath = ''
      this.signaturePreview = ''
    },
    


    // 获取单位列表
    fetchUnitList() {
      this.loading = true;
      
      // 查询所有单位信息
      getAllUnits()
        .then(response => {
if (response.data && response.data.success) {
// 直接使用返回的数据，因为新接口返回的是数组格式
            this.unitOptions = response.data.data || [];
            
} else {
            console.error('获取单位列表失败，响应消息:', response.data.message);
            this.$message.error(response.data.message || '获取单位列表失败');
            this.unitOptions = [];
          }
        })
        .catch(error => {
          console.error('获取单位列表失败，错误:', error);
          this.$message.error('获取单位列表失败，请稍后重试');
          this.unitOptions = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    // 获取项目列表（根据单位ID）
    fetchProjectList() {
      if (!this.userForm.unit_id) {
        console.warn('单位ID不存在，无法获取项目列表');
        this.projectOptions = [];
        return Promise.resolve();
      }
      
      return getProjectsByUnit(this.userForm.unit_id)
        .then(response => {
if (response.data && response.data.success) {
            this.projectOptions = response.data.data || [];
} else {
            console.error('获取项目列表失败:', response.data?.message);
            this.$message.error(response.data?.message || '获取项目列表失败');
            this.projectOptions = [];
          }
          return response;
        })
        .catch(error => {
          console.error('获取项目列表失败:', error);
          this.$message.error('获取项目列表失败，请稍后重试');
          this.projectOptions = [];
          throw error;
        });
    }
  }
}
</script>

<style lang="scss">
/* 全局对话框样式，确保对话框在页面中心 */
.el-dialog__wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  
  .el-dialog {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 60px);   /* 限制最大高度 */
    
    .el-dialog__body {
      overflow-y: auto;               /* 内容过多时出现滚动条 */
      flex: 1;
    }
  }
}
</style>

<style lang="scss" scoped>
.user-info-container {
  background-color: #fff;
  padding: 20px;
  min-height: calc(100vh - 120px);
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  
  .page-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    
    .blue-bar {
      width: 3px;
      background-color: #1890ff;
      height: 16px;
      margin-right: 8px;
    }
    
    .page-title {
      font-size: 16px;
      font-weight: 500;
      color: #333;
      margin: 0;
    }
  }
  
  .search-area {
    margin-bottom: 20px;
    
    .search-inputs {
      display: flex;
      flex-wrap: wrap;
      
      .search-item {
        margin-right: 15px;
        margin-bottom: 10px;
        width: 200px;
      }
      
      .search-buttons {
        display: flex;
        align-items: flex-start;
        
        .el-button {
          margin-right: 10px;
        }
      }
    }
  }
  
  .table-container {
    .el-table {
      margin-bottom: 20px;
    }
    
    .edit-btn {
      color: #1890ff;
      margin-right: 15px;
    }
    
    .reset-password-btn {
      color: #e6a23c;
      margin-right: 15px;
    }
    
    .delete-btn {
      color: #f56c6c;
    }
  }
  
  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }
  
  .delete-dialog-content {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 30px 0;
    
    .warning-icon {
      font-size: 24px;
      color: #e6a23c;
      margin-right: 10px;
    }
    
    span {
      font-size: 16px;
    }
  }
  
  .user-dialog {
    :deep(.el-dialog__header) {
      background-color: #f8f9fc;
      padding: 15px 20px;
      text-align: center;
    }
    
    :deep(.el-dialog__title) {
      font-size: 16px;
      font-weight: 500;
      color: #333;
    }
    
    :deep(.el-dialog__body) {
      padding: 20px;
    }
    
    :deep(.el-form-item__label) {
      font-weight: normal;
      color: #606266;
    }
    
    :deep(.el-input__inner) {
      border-radius: 2px;
    }
    
    :deep(.el-dialog__footer) {
      padding: 10px 20px 20px;
      text-align: center;
    }
    
    :deep(.el-dialog__headerbtn) {
      top: 15px;
    }
  }
  
  // 删除确认对话框样式
  :deep(.el-dialog) {
    border-radius: 4px;
    overflow: hidden;
  }
  
  // 角色选择下拉框样式
  :deep(.el-select-dropdown__item) {
    height: auto;
    line-height: 1.5;
    padding: 12px 20px;
    
    span {
      display: block;
      width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  
  // 签名图片上传样式
  .signature-uploader {
    :deep(.el-upload) {
      border: 1px dashed #d9d9d9;
      border-radius: 6px;
      cursor: pointer;
      position: relative;
      overflow: hidden;
      transition: border-color 0.3s;
      
      &:hover {
        border-color: #409EFF;
      }
    }
    
    .signature-image {
      width: 178px;
      height: 178px;
      display: block;
      object-fit: contain;
    }
    
    .upload-placeholder {
      width: 178px;
      height: 178px;
      line-height: 178px;
      text-align: center;
      background-color: #fafafa;
      
      .signature-uploader-icon {
        font-size: 28px;
        color: #8c939d;
      }
    }
    
    .el-upload__tip {
      color: #606266;
      font-size: 12px;
      margin-top: 7px;
    }
    
    .file-path {
      margin-top: 10px;
    }
  }
}
</style>




